import { getDistributionTimezone } from "../../services/peopleDistributionTypes";

const ONE_HOUR_MS = 60 * 60 * 1000;

/** Fixed daily window: 12 AM (hour 0) through 11 PM (hour 23). */
export const DAY_AXIS_START_HOUR = 0;
export const DAY_AXIS_END_HOUR = 23;

/** Resolve UTC ms for a wall-clock hour on a calendar date in the given timezone. */
export function getWallClockMs(date: string, hour: number, timezone: string): number {
  const [year, month, day] = date.split("-").map(Number);
  let candidate = Date.UTC(year, month - 1, day, hour, 30, 0);

  for (let attempt = 0; attempt < 72; attempt++) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(new Date(candidate));

    const y = Number(parts.find((part) => part.type === "year")?.value);
    const mo = Number(parts.find((part) => part.type === "month")?.value);
    const d = Number(parts.find((part) => part.type === "day")?.value);
    let h = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
    if (h === 24) h = 0;
    const minute = Number(parts.find((part) => part.type === "minute")?.value ?? 0);

    if (y === year && mo === month && d === day && h === hour && minute === 0) {
      return candidate;
    }

    const dayDiff = day - d;
    const hourDiff = hour - h;
    candidate += (dayDiff * 24 * 3600 + hourDiff * 3600 - minute * 60) * 1000;
  }

  return new Date(year, month - 1, day, hour, 0, 0, 0).getTime();
}

/** Resolve UTC ms for a wall-clock hour:minute on a calendar date in the given timezone. */
export function getWallClockMsWithMinute(
  date: string,
  hour: number,
  minute: number,
  timezone: string
): number {
  const [year, month, day] = date.split("-").map(Number);
  let candidate = Date.UTC(year, month - 1, day, hour, minute, 0);

  for (let attempt = 0; attempt < 72; attempt++) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(new Date(candidate));

    const y = Number(parts.find((part) => part.type === "year")?.value);
    const mo = Number(parts.find((part) => part.type === "month")?.value);
    const d = Number(parts.find((part) => part.type === "day")?.value);
    let h = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
    if (h === 24) h = 0;
    const m = Number(parts.find((part) => part.type === "minute")?.value ?? 0);

    if (y === year && mo === month && d === day && h === hour && m === minute) {
      return candidate;
    }

    const dayDiff = day - d;
    const hourDiff = hour - h;
    const minuteDiff = minute - m;
    candidate += (dayDiff * 24 * 3600 + hourDiff * 3600 + minuteDiff * 60) * 1000;
  }

  return new Date(year, month - 1, day, hour, minute, 0).getTime();
}

/** Parse KPI peak labels such as "8:12 AM" or "08:12" to chart axis milliseconds. */
export function peakTimeLabelToMs(
  peakTime: string,
  selectedDate: string,
  timezone: string
): number | null {
  if (!peakTime) return null;

  if (peakTime.includes("T")) {
    const parsed = Date.parse(peakTime);
    return Number.isNaN(parsed) ? null : parsed;
  }

  const match = peakTime.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;

  let hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  if (/pm/i.test(peakTime) && hour < 12) hour += 12;
  if (/am/i.test(peakTime) && hour === 12) hour = 0;

  return getWallClockMsWithMinute(selectedDate, hour, minute, timezone);
}

/** Build one tick per hour from 12 AM through 11 PM. */
export function buildHourlyTicks(date: string, timezone: string): number[] {
  return Array.from(
    { length: DAY_AXIS_END_HOUR - DAY_AXIS_START_HOUR + 1 },
    (_, index) => getWallClockMs(date, DAY_AXIS_START_HOUR + index, timezone)
  );
}

/** Always 12 AM → 11 PM on the selected calendar day. */
export function getFixedDayAxisRange(
  date: string,
  timezone = getDistributionTimezone()
): { min: number; max: number } {
  const ticks = buildHourlyTicks(date, timezone);
  return {
    min: ticks[0],
    max: ticks[ticks.length - 1] + ONE_HOUR_MS,
  };
}

export function formatHourAxisLabel(timestamp: number, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    hour12: true,
  }).format(new Date(timestamp));
}

export function buildTimeAxisConfig(
  selectedDate: string,
  timezone: string,
  axisColor: string,
  gridColor: string
) {
  const { min, max } = getFixedDayAxisRange(selectedDate, timezone);
  const hourlyTicks = buildHourlyTicks(selectedDate, timezone);

  return {
    type: "time" as const,
    min,
    max,
    minInterval: ONE_HOUR_MS,
    maxInterval: ONE_HOUR_MS,
    splitNumber: hourlyTicks.length - 1,
    axisLine: { lineStyle: { color: gridColor } },
    axisTick: {
      alignWithLabel: true,
      interval: 0,
    },
    splitLine: {
      show: true,
      lineStyle: { color: gridColor, type: "dashed" as const, opacity: 0.35 },
    },
    axisLabel: {
      color: axisColor,
      fontSize: 10,
      hideOverlap: false,
      showMinLabel: true,
      showMaxLabel: true,
      customValues: hourlyTicks,
      formatter: (value: number) => formatHourAxisLabel(value, timezone),
    },
  };
}

export function toTimeSeriesData(
  bucketTimes: string[],
  values: number[]
): [number, number][] {
  return bucketTimes.map((iso, index) => [new Date(iso).getTime(), values[index] ?? 0]);
}

/** Calendar minute key in the dashboard timezone (for matching 1-min buckets). */
export function getZonedMinuteKey(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));

  const year = parts.find((part) => part.type === "year")?.value ?? "0000";
  const month = parts.find((part) => part.type === "month")?.value ?? "00";
  const day = parts.find((part) => part.type === "day")?.value ?? "00";
  let hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  if (hour === "24") hour = "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export function formatPeakMarkLabel(timestamp: number, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(timestamp));
}

/** Tooltip header time in 12-hour format with the dashboard timezone. */
export function formatAxisTooltipTime(timestamp: number, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(new Date(timestamp));
}

/** Resolve chart peak x-position from KPI bucket time or the max data point. */
export function resolveChartPeakTimestamp(
  data: { bucketTime: string; totalOccupancy: number }[],
  timezone: string,
  peakBucketTime?: string,
  peakOccupancy?: number
): number | null {
  if (peakBucketTime) {
    const targetKey = getZonedMinuteKey(peakBucketTime, timezone);
    const match = data.find(
      (point) => getZonedMinuteKey(point.bucketTime, timezone) === targetKey
    );
    if (match) return new Date(match.bucketTime).getTime();
    return new Date(peakBucketTime).getTime();
  }

  let peakIndex = -1;
  let peakValue = 0;
  data.forEach((point, index) => {
    const meetsTarget = peakOccupancy == null || point.totalOccupancy === peakOccupancy;
    if (meetsTarget && point.totalOccupancy >= peakValue) {
      peakValue = point.totalOccupancy;
      peakIndex = index;
    }
  });

  if (peakIndex < 0) {
    data.forEach((point, index) => {
      if (point.totalOccupancy >= peakValue) {
        peakValue = point.totalOccupancy;
        peakIndex = index;
      }
    });
  }

  return peakIndex >= 0 ? new Date(data[peakIndex].bucketTime).getTime() : null;
}

const DEFAULT_DATA_ZOOM_WINDOW_MINUTES = 120;

/** Map the latest data window to 0–100% along the fixed day axis. */
export function getRecentWindowDataZoomRange(
  bucketTimes: string[],
  selectedDate: string,
  timezone: string,
  windowMinutes = DEFAULT_DATA_ZOOM_WINDOW_MINUTES
): { start: number; end: number } {
  if (!bucketTimes.length) {
    return { start: 0, end: 100 };
  }

  const { min, max } = getFixedDayAxisRange(selectedDate, timezone);
  const range = max - min;
  if (range <= 0) {
    return { start: 0, end: 100 };
  }

  const lastTs = new Date(bucketTimes[bucketTimes.length - 1]).getTime();
  const visibleEnd = Math.min(lastTs, max);
  const visibleStart = Math.max(min, visibleEnd - windowMinutes * 60_000);

  return {
    start: ((visibleStart - min) / range) * 100,
    end: ((visibleEnd - min) / range) * 100,
  };
}

/** Stock-chart style inside pan/zoom + bottom slider for trend charts. */
export function buildTrendChartDataZoom(
  isDark: boolean,
  axisColor: string,
  accentColor: string,
  start: number,
  end: number
) {
  return [
    {
      type: "inside" as const,
      xAxisIndex: 0,
      start,
      end,
      zoomOnMouseWheel: true,
      moveOnMouseWheel: true,
      moveOnMouseMove: true,
      zoomLock: false,
    },
    {
      type: "slider" as const,
      xAxisIndex: 0,
      start,
      end,
      height: 20,
      bottom: 8,
      showDetail: false,
      borderColor: "transparent",
      fillerColor: isDark ? `${accentColor}4D` : `${accentColor}40`,
      handleStyle: {
        color: accentColor,
        borderColor: accentColor,
      },
      textStyle: { color: axisColor, fontSize: 10 },
      dataBackground: {
        lineStyle: { color: "transparent" },
        areaStyle: {
          color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        },
      },
    },
  ];
}
